import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Money } from '../../components/Money';
import { Button } from '../../components/Button';
import { StatusPill } from '../../components/StatusPill';
import { cn } from '../../lib/cn';

interface OwnerDashboard {
  period: { label: string; from: string; to: string };
  kpis: {
    netMTD: string;
    totalMTD: string;
    vatMTD: string;
    orderCount: number;
    deltaVsLastMonthPct: number | null;
  };
  sparkline: Array<{ day: string; value: number }>;
  cash: { collectedToDate: string; closedToDate: string; inTransit: string };
  receivables: {
    buckets: { '0-30': number; '31-60': number; '61-90': number; '90+': number };
    total: number;
  };
  byShop: Array<{
    shop: { id: string; code: string; name: string };
    orderCount: number;
    total: string;
    netSales: string;
  }>;
  cashPerShop: Array<{
    shop: { id: string; code: string; name: string };
    inDrawer: string;
    inTransit: string;
    lastCollectionAt: string | null;
  }>;
  topCommissions: Array<{
    userId: string;
    name: string;
    shop: { code: string; name: string } | null;
    netSales: string;
    commission: string;
  }>;
  alerts: Array<{
    kind: string;
    tone: 'info' | 'warning' | 'negative';
    title: string;
    body: string;
    at?: string;
  }>;
}

export function FinanceDashboard(): JSX.Element {
  const data = useQuery({
    queryKey: ['owner-dashboard'],
    queryFn: () => apiGet<OwnerDashboard>('/dashboards/owner'),
    refetchInterval: 90_000,
  });

  if (data.isLoading || !data.data) {
    return (
      <PageScaffold title="Finance overview">
        <div className="text-ink-3 text-center py-20">Loading…</div>
      </PageScaffold>
    );
  }
  const d = data.data;

  return (
    <PageScaffold
      title="Finance overview"
      subtitle={
        <>
          All shops · {d.period.label} ·{' '}
          <Link to="/finance/reports" className="text-accent hover:underline">
            Open reports →
          </Link>
        </>
      }
      actions={
        <>
          <Link to="/finance/reports">
            <Button variant="secondary" size="sm">
              Reports
            </Button>
          </Link>
          <Link to="/finance/receivables">
            <Button variant="secondary" size="sm">
              Receivables
            </Button>
          </Link>
          <Link to="/finance/vat">
            <Button variant="secondary" size="sm">
              VAT
            </Button>
          </Link>
          <Link to="/finance/commission">
            <Button variant="secondary" size="sm">
              Commissions
            </Button>
          </Link>
        </>
      }
    >
      {/* ── HERO row ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1 lg:col-span-2 rounded-lg bg-ink text-paper-2 p-6 flex flex-col justify-between min-h-[200px]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-4 font-semibold">
              Sales (incl. VAT) · {d.period.label}
            </div>
            <Money value={d.kpis.totalMTD} className="kc-serif text-5xl text-paper-2 mt-2 block" />
            <div className="text-xs text-ink-4 mt-3">
              {d.kpis.deltaVsLastMonthPct != null && (
                <>
                  vs last month{' '}
                  <b
                    className={
                      d.kpis.deltaVsLastMonthPct >= 0 ? 'text-positive-soft' : 'text-negative-soft'
                    }
                  >
                    {d.kpis.deltaVsLastMonthPct >= 0 ? '+' : ''}
                    {d.kpis.deltaVsLastMonthPct.toFixed(1)}%
                  </b>{' '}
                  ·{' '}
                </>
              )}
              <b className="text-paper-2">{d.kpis.orderCount}</b> orders
            </div>
          </div>
          <Sparkline points={d.sparkline.map((p) => p.value)} />
        </div>
        <KpiCard
          label="VAT collected"
          value={<Money value={d.kpis.vatMTD} />}
          hint="15% on net sales"
          tone="info"
        />
        <KpiCard
          label="Receivables outstanding"
          value={<Money value={d.receivables.total} />}
          hint={`${Math.round(((d.receivables.buckets['0-30'] || 0) / (d.receivables.total || 1)) * 100)}% within 30d`}
          tone="warning"
        />
      </div>

      {/* ── Mid row: sales by shop · AR aging · alerts ───────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card
          title="Sales by shop · MTD"
          subtitle="Sorted by total · click to view invoices"
          className="lg:col-span-1"
        >
          {d.byShop.length === 0 ? (
            <div className="text-sm text-ink-3 text-center py-4">No sales in this period</div>
          ) : (
            <>
              <div className="max-h-[220px] overflow-y-auto overflow-x-hidden space-y-2">
                {d.byShop.map((s, i) => {
                  const max = Math.max(...d.byShop.map((x) => Number(x.total)), 1);
                  const pct = (Number(s.total) / max) * 100;
                  return (
                    <Link
                      key={s.shop.id}
                      to={`/orders?shopId=${s.shop.id}`}
                      className="block text-sm rounded px-2 py-2 hover:bg-paper-2 transition"
                    >
                      <div className="flex items-baseline justify-between mb-1 gap-2">
                        <span className="text-ink min-w-0 truncate">
                          <span className="kc-mono text-xs text-ink-3 mr-2">{s.shop.code}</span>
                          {s.shop.name}
                        </span>
                        <Money value={s.total} className="text-xs shrink-0" />
                      </div>
                      <div className="h-3 bg-rule-2 rounded overflow-hidden">
                        <div
                          className={
                            i === 0 ? 'h-full bg-accent rounded' : 'h-full bg-ink-2 rounded'
                          }
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-ink-3 mt-1 kc-mono">
                        {s.orderCount} orders
                      </div>
                    </Link>
                  );
                })}
              </div>
              <Link
                to="/finance/reports?groupBy=shop"
                className="block text-xs text-accent hover:underline text-center mt-3 pt-3 border-t border-rule-2"
              >
                Open per-shop reports →
              </Link>
            </>
          )}
        </Card>

        <Card title="Receivables · aging">
          <ReceivablesAging buckets={d.receivables.buckets} total={d.receivables.total} />
          <Link
            to="/finance/receivables"
            className="inline-block mt-3 text-xs text-accent hover:underline"
          >
            Open AR aging →
          </Link>
        </Card>

        <Card
          title="Heads up"
          action={
            d.alerts.length > 0 ? <StatusPill tone="negative">{d.alerts.length}</StatusPill> : null
          }
        >
          {d.alerts.length === 0 ? (
            <div className="text-sm text-ink-3 text-center py-4">Nothing urgent ✓</div>
          ) : (
            <ul className="space-y-3">
              {d.alerts.slice(0, 6).map((a, i) => (
                <li key={i} className="flex gap-2.5 text-sm">
                  <span
                    className={cn('mt-1.5 w-1.5 h-1.5 rounded-full shrink-0', {
                      'bg-negative': a.tone === 'negative',
                      'bg-warning': a.tone === 'warning',
                      'bg-info': a.tone === 'info',
                    })}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-ink truncate">{a.title}</span>
                      {a.at && (
                        <span className="text-[10px] text-ink-4 shrink-0">
                          {new Date(a.at).toLocaleTimeString('en-MU', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-3 mt-0.5">{a.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* ── Bottom row: commissions · cash position · cash trail ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Top commissions · MTD" subtitle="By estimated commission accrued">
          {d.topCommissions.length === 0 ? (
            <div className="text-sm text-ink-3 text-center py-4">No sales yet</div>
          ) : (
            <ol className="space-y-2.5 max-h-[200px] overflow-y-auto overflow-x-hidden">
              {d.topCommissions.map((c, i) => (
                <li key={c.userId} className="flex items-center justify-between text-sm">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="kc-mono text-xs text-ink-3 w-5">{i + 1}.</span>
                    <span className="text-ink truncate">{c.name.split(' (')[0]}</span>
                    {c.shop && (
                      <span className="text-[10px] text-ink-3 kc-mono">· {c.shop.code}</span>
                    )}
                  </div>
                  <Money value={c.commission} className="text-xs font-medium" />
                </li>
              ))}
            </ol>
          )}
          <Link
            to="/finance/commission"
            className="inline-block mt-3 text-xs text-accent hover:underline"
          >
            Open commissions →
          </Link>
        </Card>

        <Card title="Cash position · all shops" subtitle="Click any shop to see its cash trail">
          <div className="max-h-[220px] overflow-y-auto overflow-x-hidden -mx-2">
            <ul className="divide-y divide-rule-2">
              {d.cashPerShop.map((row) => {
                const hasTransit = row.inTransit !== '0';
                return (
                  <li key={row.shop.id}>
                    <Link
                      to={`/cash-close?shopId=${row.shop.id}`}
                      className="block px-2 py-2.5 hover:bg-paper-2 transition rounded"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm text-ink min-w-0 truncate">
                          <span className="kc-mono text-xs text-ink-3 mr-2">{row.shop.code}</span>
                          {row.shop.name}
                        </span>
                        <Money
                          value={row.inDrawer}
                          muted={row.inDrawer === '0'}
                          className="text-sm shrink-0"
                        />
                      </div>
                      <div className="text-[11px] text-ink-3 mt-1 flex items-center gap-2">
                        {hasTransit && (
                          <>
                            <Money value={row.inTransit} className="text-warning" />
                            <span>in transit</span>
                            <span aria-hidden>·</span>
                          </>
                        )}
                        <span>
                          last collected{' '}
                          {row.lastCollectionAt
                            ? new Date(row.lastCollectionAt).toLocaleDateString('en-MU', {
                                day: 'numeric',
                                month: 'short',
                              })
                            : '—'}
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </Card>

        <Card title="Cash trail">
          <dl className="space-y-3 text-sm">
            <Row
              label="Collected to date (cash)"
              value={<Money value={d.cash.collectedToDate} />}
            />
            <Row label="Closed to date" value={<Money value={d.cash.closedToDate} />} />
            <Row
              label="In transit (HQ ↔ shop)"
              value={
                <Money
                  value={d.cash.inTransit}
                  className={d.cash.inTransit !== '0' ? 'text-warning' : ''}
                />
              }
            />
          </dl>
          <Link to="/cash-close" className="inline-block mt-3 text-xs text-accent hover:underline">
            Open cash close →
          </Link>
        </Card>
      </div>
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: 'info' | 'warning' | 'negative' | 'neutral';
}): JSX.Element {
  const ring: Record<NonNullable<typeof tone>, string> = {
    info: 'border-info/30',
    warning: 'border-warning/30',
    negative: 'border-negative/30',
    neutral: 'border-rule',
  };
  return (
    <div
      className={cn(
        'rounded-lg border bg-surface p-5 flex flex-col justify-between min-h-[200px]',
        ring[tone],
      )}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</div>
      <div>
        <div className="text-3xl font-medium text-ink kc-serif mt-2">{value}</div>
        {hint && <div className="text-xs text-ink-3 mt-2">{hint}</div>}
      </div>
    </div>
  );
}

function ReceivablesAging({
  buckets,
  total,
}: {
  buckets: { '0-30': number; '31-60': number; '61-90': number; '90+': number };
  total: number;
}): JSX.Element {
  const segments: Array<{
    label: string;
    v: number;
    color: string;
    tone: 'positive' | 'warning' | 'negative';
  }> = [
    { label: '0–30 days', v: buckets['0-30'], color: 'var(--positive)', tone: 'positive' },
    { label: '31–60 days', v: buckets['31-60'], color: 'var(--warning)', tone: 'warning' },
    { label: '61–90 days', v: buckets['61-90'], color: '#d97757', tone: 'warning' },
    { label: '90+ days', v: buckets['90+'], color: 'var(--negative)', tone: 'negative' },
  ];
  const safeTotal = total || 1;
  return (
    <div>
      {total > 0 && (
        <div className="h-2.5 rounded overflow-hidden flex">
          {segments.map((s) => (
            <div
              key={s.label}
              style={{ width: `${(s.v / safeTotal) * 100}%`, background: s.color }}
            />
          ))}
        </div>
      )}
      <div className="mt-3 space-y-1.5 text-sm">
        {segments.map((s) => {
          const pct = total > 0 ? ((s.v / total) * 100).toFixed(0) : '0';
          return (
            <div key={s.label} className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-ink-2">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                {s.label}
              </span>
              <span className="flex items-center gap-3 text-xs">
                <Money value={s.v} muted={s.v === 0} className="kc-mono" />
                <span className="text-ink-3 w-10 text-right kc-mono">{pct}%</span>
              </span>
            </div>
          );
        })}
        <div className="border-t border-rule-2 pt-2 mt-2 flex items-center justify-between text-sm font-medium">
          <span className="text-ink">Total open</span>
          <Money value={total} className="text-accent" />
        </div>
      </div>
    </div>
  );
}

function Sparkline({ points }: { points: number[] }): JSX.Element {
  const w = 280;
  const h = 60;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'} ${(i / Math.max(points.length - 1, 1)) * w},${h - ((p - min) / range) * h}`,
    )
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12 mt-2" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkfill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(246,241,232,0.15)" />
          <stop offset="100%" stopColor="rgba(246,241,232,0)" />
        </linearGradient>
      </defs>
      <path d={`${path} L ${w},${h} L 0,${h} Z`} fill="url(#sparkfill)" />
      <path
        d={path}
        stroke="rgb(246,241,232)"
        strokeWidth={1.5}
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between">
      <span className="text-ink-3">{label}</span>
      <span className="kc-mono kc-tnum text-ink">{value}</span>
    </div>
  );
}
